import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Modal from '@/components/common/Modal/Modal';
import { getUID } from '@/utils';
import styles from './NotificationModal.module.css';

/**
 * NotificationModal Component
 * Modal for viewing notifications (unread and read)
 * Opens via route: /modal?mode=notification
 * Full screen on mobile, modal on desktop
 */
const NotificationModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('unread'); // 'unread' | 'read'
  const [notifications, setNotifications] = useState({ unread: [], read: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Check if modal should be open based on query params
    if (location.pathname === '/modal' && mode === 'notification') {
      setIsOpen(true);
      fetchNotifications();
    } else {
      setIsOpen(false);
    }
  }, [location.pathname, mode]);

  const fetchUnreadCount = async () => {
    try {
      const uid = getUID();
      if (!uid) return;
      const response = await fetch(`https://api.jackopot.com/firebase/unread-count/${uid}`);
      const data = await response.json();
      if (data.success) {
        setUnreadCount(data.unreadCount);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const uid = getUID();
      if (!uid) {
        setIsLoading(false);
        return;
      }
      const response = await fetch(`https://api.jackopot.com/firebase/history/${uid}`);
      const data = await response.json();
      if (data.success) {
        setNotifications({
          unread: data.notifications?.unread || [],
          read: data.notifications?.read || []
        });
        await fetchUnreadCount();
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      const uid = getUID();
      if (!uid) return;
      const response = await fetch('https://api.jackopot.com/firebase/mark-as-read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: uid,
          notificationIds: [id]
        })
      });

      if (response.ok) {
        setNotifications(prev => {
          const updatedUnread = prev.unread.filter(n => n.id !== id);
          const movedNotification = prev.unread.find(n => n.id === id);
          return {
            unread: updatedUnread,
            read: movedNotification ? [...prev.read, { ...movedNotification, is_read: true }] : prev.read
          };
        });
        await fetchUnreadCount();
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const uid = getUID();
      if (!uid) return;
      const unreadIds = notifications.unread.map(notification => notification.id);
      if (unreadIds.length === 0) return;
      
      const response = await fetch('https://api.jackopot.com/firebase/mark-as-read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: uid,
          notificationIds: unreadIds
        })
      });

      if (response.ok) {
        setNotifications(prev => ({
          unread: [],
          read: [...prev.read, ...prev.unread.map(n => ({ ...n, is_read: true }))]
        }));
        await fetchUnreadCount();
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    // Remove mode query param and navigate to home
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('mode');
    navigate(`/?${newSearchParams.toString()}`, { replace: true });
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const currentNotifications = activeTab === 'unread' ? notifications.unread : notifications.read;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      title="Notifications"
      showCloseButton={true}
    >
      <div className={styles.modalBody}>
        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'unread' ? styles.tabActive : ''}`}
            onClick={() => switchTab('unread')}
          >
            Unread {notifications.unread.length > 0 && (
              <span className={styles.badge}>{notifications.unread.length}</span>
            )}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'read' ? styles.tabActive : ''}`}
            onClick={() => switchTab('read')}
          >
            Read
          </button>
        </div>

        {/* Mark All as Read Button (only for unread tab) */}
        {activeTab === 'unread' && notifications.unread.length > 0 && (
          <div className={styles.markAllContainer}>
            <button
              className={styles.markAllButton}
              onClick={markAllAsRead}
            >
              Mark all as read
            </button>
          </div>
        )}

        {/* Tab Content */}
        <div className={styles.tabContent}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <p>Loading notifications...</p>
            </div>
          ) : currentNotifications.length === 0 ? (
            <div className={styles.emptyContainer}>
              <p className={styles.emptyText}>
                {activeTab === 'unread' ? 'No unread notifications' : 'No read notifications'}
              </p>
            </div>
          ) : (
            <div className={styles.notificationsList}>
              {currentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`${styles.notificationItem} ${!notification.is_read ? styles.unread : ''}`}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                >
                  <div className={styles.notificationContent}>
                    <h4 className={styles.notificationTitle}>{notification.title || 'Notification'}</h4>
                    <p className={styles.notificationMessage}>{notification.message || notification.content || ''}</p>
                    <span className={styles.notificationTime}>
                      {formatTimestamp(notification.timestamp || notification.date)}
                    </span>
                  </div>
                  {!notification.is_read && (
                    <div className={styles.unreadIndicator}></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default NotificationModal;
